import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, XCircle, RefreshCcw, Filter, ExternalLink, Copy, Trash2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("tr-TR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatMoney(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 2,
  }).format(n);
}

export default function DraftHistoryPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState("all");
  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  async function doClearAll() {
    setClearing(true);
    try {
      const { data } = await api.delete("/drafts/history");
      toast.success("Geçmiş temizlendi", {
        description: `${data.logs_deleted} kayıt silindi · ${data.users_reset} kullanıcı sayacı sıfırlandı.`,
      });
      setClearOpen(false);
      await load();
    } catch (e) {
      toast.error("Temizleme başarısız", { description: formatApiError(e) });
    } finally {
      setClearing(false);
    }
  }

  async function load() {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (isAdmin && userFilter && userFilter !== "all") params.user_id = userFilter;
      const { data } = await api.get("/drafts/history", { params });
      setLogs(data.logs || []);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFilter]);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/users");
        setUsers(data || []);
      } catch (_) {}
    })();
  }, [isAdmin]);

  return (
    <div>
      <PageHeader
        subtitle="Taslak sipariş kayıtları · denetim izi"
        title="Taslak Geçmişi"
        actions={
          <>
            {isAdmin ? (
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-zinc-500" />
                <Select value={userFilter} onValueChange={setUserFilter}>
                  <SelectTrigger
                    className="w-[220px] rounded-sm border-zinc-300 h-9"
                    data-testid="history-user-filter"
                  >
                    <SelectValue placeholder="Tüm kullanıcılar" />
                  </SelectTrigger>
                  <SelectContent className="rounded-sm">
                    <SelectItem value="all">Tüm kullanıcılar</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} · {u.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              className="rounded-sm border-zinc-300 h-9"
              data-testid="refresh-history-btn"
            >
              <RefreshCcw className="h-4 w-4 mr-1.5" />
              Yenile
            </Button>
            {isAdmin ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setClearOpen(true)}
                className="rounded-sm border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800 h-9"
                data-testid="clear-history-btn"
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                Tümünü Temizle
              </Button>
            ) : null}
          </>
        }
      />

      <div className="max-w-[1400px] mx-auto px-6 py-8">
        {error ? (
          <div className="rounded-sm border border-red-200 bg-red-50 p-4 mb-6 text-sm text-red-800">
            {error}
          </div>
        ) : null}

        <div className="border border-zinc-200 bg-white">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50">
                <th className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 font-semibold">
                  Tarih
                </th>
                {isAdmin ? (
                  <th className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 font-semibold">
                    Operatör
                  </th>
                ) : null}
                <th className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 font-semibold">
                  Kaynak Sepet
                </th>
                <th className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 font-semibold">
                  Taslak
                </th>
                <th className="px-3 py-2 text-right text-[10px] font-mono uppercase tracking-widest text-zinc-500 font-semibold">
                  Tutar
                </th>
                <th className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 font-semibold">
                  Durum
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-zinc-200">
                    <td colSpan={isAdmin ? 6 : 5} className="px-3 py-3">
                      <Skeleton className="h-4 w-full rounded-sm" />
                    </td>
                  </tr>
                ))
              ) : logs.length === 0 ? (
                <tr>
                  <td
                    colSpan={isAdmin ? 6 : 5}
                    className="px-3 py-16 text-center text-sm text-zinc-500"
                    data-testid="history-empty"
                  >
                    Henüz taslak sipariş kaydı yok.
                  </td>
                </tr>
              ) : (
                logs.map((l) => (
                  <tr
                    key={l.id}
                    className="row-hover border-b border-zinc-200 bg-white"
                    data-testid={`history-row-${l.id}`}
                  >
                    <td className="px-3 py-3 text-sm">
                      <div className="font-mono text-xs">{formatDate(l.created_at)}</div>
                    </td>
                    {isAdmin ? (
                      <td className="px-3 py-3 text-sm">
                        <div className="font-medium text-zinc-950">
                          {l.created_by_name || "—"}
                        </div>
                        <div className="font-mono text-[11px] text-zinc-500">
                          {l.created_by_email}
                        </div>
                      </td>
                    ) : null}
                    <td className="px-3 py-3 text-sm font-mono">
                      #{String(l.source_cart_id)}
                      {l.source_cart_item_count != null ? (
                        <span className="ml-2 text-[10px] text-zinc-400">
                          {l.source_cart_item_count} kalem
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-3 text-sm font-mono">
                      {l.draft_order_url ? (
                        <div className="flex items-center gap-1.5">
                          <a
                            href={l.draft_order_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[#002FA7] hover:underline"
                            data-testid={`history-link-${l.id}`}
                            title={l.draft_order_url}
                          >
                            <ExternalLink className="h-3 w-3" />
                            {l.draft_order_name || (l.draft_order_id != null ? `#${l.draft_order_id}` : "aç")}
                          </a>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(l.draft_order_url);
                                toast.success("Link kopyalandı");
                              } catch {
                                toast.error("Kopyalama başarısız");
                              }
                            }}
                            className="rounded-sm border border-zinc-200 hover:bg-zinc-50 p-1 text-zinc-600 hover:text-zinc-900"
                            data-testid={`history-copy-${l.id}`}
                            title="Linki kopyala"
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                        </div>
                      ) : l.draft_order_name ? (
                        <span>{l.draft_order_name}</span>
                      ) : l.draft_order_id != null ? (
                        `#${l.draft_order_id}`
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-3 py-3 text-right text-sm font-mono tabular-nums">
                      {formatMoney(l.source_cart_total)}
                    </td>
                    <td className="px-3 py-3">
                      {l.draft_order_status === "success" ? (
                        <Badge
                          className="rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono text-[10px] uppercase tracking-wider"
                          data-testid={`status-${l.id}`}
                        >
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Başarılı
                        </Badge>
                      ) : (
                        <div>
                          <Badge
                            className="rounded-sm bg-red-50 text-red-700 border border-red-200 font-mono text-[10px] uppercase tracking-wider"
                            data-testid={`status-${l.id}`}
                          >
                            <XCircle className="h-3 w-3 mr-1" />
                            Hata
                          </Badge>
                          {l.error_message ? (
                            <div
                              className="mt-1 text-[11px] text-red-700 max-w-[280px] truncate"
                              title={l.error_message}
                            >
                              {l.error_message}
                            </div>
                          ) : null}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={clearOpen} onOpenChange={(v) => (clearing ? null : setClearOpen(v))}>
        <DialogContent className="rounded-sm max-w-md" data-testid="clear-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" />
              Tüm geçmişi temizle?
            </DialogTitle>
            <DialogDescription>
              Bu işlem <b>uygulamadaki tüm taslak kayıtlarını siler</b> ve her kullanıcının
              sayacını <span className="font-mono">_001</span>'e sıfırlar. IdeaSoft mağazasındaki
              taslak siparişler <b>etkilenmez</b> — onlar orada kalır. Geri alınamaz.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setClearOpen(false)}
              disabled={clearing}
              className="rounded-sm"
              data-testid="clear-history-cancel"
            >
              Vazgeç
            </Button>
            <Button
              onClick={doClearAll}
              disabled={clearing}
              className="rounded-sm bg-red-600 hover:bg-red-700 text-white"
              data-testid="clear-history-confirm"
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              {clearing ? "Temizleniyor…" : "Evet, hepsini sil"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
